import React, { useState, useEffect, useRef, memo, useCallback } from "react";
import {
    Box, Button, TextField, Typography, Grid, FormControl, Checkbox,
    FormControlLabel,
    Switch, Snackbar,
    Alert,
    AlertProps, InputAdornment, Dialog, DialogActions, DialogTitle, DialogContent, Stack
} from "@mui/material";

import {
    InventoryJournalHeaderViewModel, InventoryJournalLineViewModel, DocumentProps, ActionRequest,
    ActionResponseResult, VariantOption, ItemVariantQuery, InventoryJournalAddItemRequest, LabelJournalHeaderViewModel
} from "../../Types/ExportTypes";
import ToolbarWithSave, { ToolbarWithSaveHandle } from "../../components/Toolbar/ToolbarWithSave";
import { useAuth, useLoading, useNavigation, useSnackbar } from "../../Context/Contexts";

import {
    LocationAutoComplete, ItemAutoComplete
} from "../../components/AutoComplete/AutoCompletes";

import { formatNumber } from '../../Utils/NumberUtils';
import InfoButton from "../../components/Tooltip/InfoTextField";
import AsyncAutocomplete from "../../components/AsyncAutoComplete";
import VariantAutoComplete from "../AutoComplete/Item/VariantAutoComplete";
import InventoryJournalLines from "./InventoryJournalLines"
import DefaultActions from "../Card/DefaultActions"
import { useTranslation } from "react-i18next";
import ScannerDialog from "../Scanner/ScannerDialog";
import CenteredCard from "../Card/CenteredCardLayout";
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';

const InventoryJournalHeader: React.FC<DocumentProps> = ({ title = "Edit Inventory", documentNo, documentType, readUrl, updateUrl, deleteUrl, history = false }) => {
    const { authService } = useAuth();
    const { goBack, navigate } = useNavigation();
    const { showSnackbar } = useSnackbar();
    const { showLoading, hideLoading } = useLoading();

    const [viewModel, setViewModel] = useState<InventoryJournalHeaderViewModel | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showAddItems, setShowAddItems] = useState(false);

    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const barcodeRef = useRef<HTMLInputElement>(null);
    const [isPostingModalOpen, setIsPostingModalOpen] = useState(false);
    const [reloadLines, setReloadLines] = useState(false);
    const { t } = useTranslation();
    const toolbarRef = useRef<ToolbarWithSaveHandle>(null);

    const showHasChangesError = useCallback(() => {
        toolbarRef.current?.showHasChangesError();
    }, []);

    const fetchViewModel = async (no: string) => {
        setIsLoading(true);
        try {
            const response = await authService.get<InventoryJournalHeaderViewModel>(readUrl);
            setViewModel(
                response
                    ? { ...response, hasChanges: false, originalValues: { ...response } }
                    : null
            );
        } finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        if (isLoading) showLoading();
        else hideLoading();
    }, [isLoading, showLoading, hideLoading]);


    useEffect(() => {
        if (documentNo) fetchViewModel(documentNo);

    }, [documentNo]);

    useEffect(() => {
        if (showAddItems) {
            barcodeRef.current?.focus();
        }
    }, [showAddItems]);

    useEffect(() => {
        if (!showAddItems) {
            setIsScannerOpen(false);
        }
    }, [showAddItems]);

    const handleSave = async () => {
        setIsLoading(true);
        try {
            var response = await authService.post<InventoryJournalHeaderViewModel>(updateUrl, viewModel);
            setViewModel(
                response
                    ? {
                        ...response, hasChanges: false, originalValues: { ...response },
                        itemNoToAdd: viewModel?.itemNoToAdd, variantIdtoAdd: viewModel?.variantIdtoAdd, barcodeToAdd: viewModel?.barcodeToAdd
                    }
                    : null
            );
            showSnackbar(`${t("dialog.savedSuccessfully")}`, "success");
        } catch (error: any) {
            showSnackbar(`${t("dialog.failedToSaveChanges")} :  ${error.response?.data ?? error.message}`, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleChanges = async (changes: Partial<InventoryJournalHeaderViewModel>) => {
        setViewModel((prev) => {
            if (!prev) return null;

            const hasChanges =
                'locationCode' in changes ||
                'targetLocationCode' in changes ||
                'description' in changes;

            return {
                ...prev,
                ...changes,
                hasChanges: hasChanges ? true : prev.hasChanges,
            };
        });
    };

    const handleChange = async (key: keyof InventoryJournalHeaderViewModel, value: any) => {


        setViewModel((prev) => {
            if (!prev) return null;

            const hasChanges =
                key === "locationCode" ||
                key === "targetLocationCode" ||
                key === "description" ||
                key === "applyDate";

            return {
                ...prev,
                [key]: value,
                hasChanges: hasChanges ? true : prev.hasChanges,
            };
        });
    };

    const cancelChanges = () => {
        setViewModel((prev) => {
            if (!prev || !prev.originalValues) return prev;
            const original = prev.originalValues;
            const changes: Partial<InventoryJournalHeaderViewModel> = {};
            for (const key in original) {
                const typedKey = key as keyof InventoryJournalHeaderViewModel;
                const originalValue = original[typedKey];
                const currentValue = prev[typedKey];
                if (originalValue !== currentValue) {
                    (changes as any)[typedKey] = originalValue;
                }
            }
             if (Object.keys(changes).length === 0) return { ...prev, hasChanges: false };
            return { ...prev, ...changes, hasChanges: false };
        });
    };



    const createLabelJournal = async (inventoryJournalNo?: string | null) => {
        if (!inventoryJournalNo) return;
        if (viewModel?.documentType == 3 && window.confirm(t("dialog.doYouWishToCreateALabelJournal"))) {
            var journal = await authService.post<LabelJournalHeaderViewModel>(`/labelJournalApi/CreateJournalFromInventoryJournal?documentNo=${viewModel?.no}`, {});
            if (journal && journal.no) window.open(`/inventory/labeljournal/edit?documentNo=${inventoryJournalNo}`, "_blank");

        }
    }


    const onPost = async (mode?: number) => {

        if (!validateViewModel()) {

            return;
        }
        if (viewModel?.documentType === 4 && (mode === undefined || mode === null)) {
            setIsPostingModalOpen(true);

            return;
        }

        setIsLoading(true);
        try {
            console.log("Posting...");
            const body: ActionRequest = {
                documentNo: viewModel?.no,
                documentType: viewModel?.documentType,
                open: viewModel?.posted === null,
                action: "Post"
            };
            if (mode !== undefined && mode !== null) {
                body.actionRequestExtraData = [{ key: "mode", value: mode.toString() }];
            }

            const response = await authService.post<ActionResponseResult>("/inventoryApi/DocumentAction", body);
            console.log("Post completed:", response);
            setIsLoading(false);

            if (viewModel?.documentType == 3 && window.confirm(t("dialog.doYouWishToCreateALabelJournal"))) {
                await createLabelJournal(viewModel?.no);
            }

            if (documentType == 2) navigate({ path: "/inventory/journalhistory/edit", params: { documentNo: response?.result ?? viewModel?.no, documentType: viewModel?.documentType, open: false } });

            else navigate({ path: "/inventory/journalhistory/edit", params: { documentNo: viewModel?.no, documentType: viewModel?.documentType, open: false } });
        } catch (error: any) {
            console.error("Error during Post:", error);
            showSnackbar(`${t("common.failedToPost")} : ${error.response?.data ?? error.message}`, "error");
        }
        finally {
            setIsLoading(false);
        }
    };


    const validateViewModel = () => {
        if (history) return false;
        if (!viewModel) {
            showSnackbar(t("dialog.inventoryMissing"), "error");

            return false;
        }

        if (!viewModel.locationCode || viewModel.locationCode.trim() === "") {
            showSnackbar(t("dialog.locationRequired"), "warning");
            return false;
        }

        if (documentType == 2 && (!viewModel.targetLocationCode || viewModel.targetLocationCode.trim() === "")) {
            showSnackbar(t("dialog.targetLocationRequired"), "warning");
            return false;
        }
        //more checks if other types
        //transfer need to set 2 locations dunno ifw e will support




        return true;
    };
    const onDelete = async () => {
        if (history) return;
        if (!window.confirm(t("dialog.areYouSure"))) return;
        setIsLoading(true);
        try {
            const body: ActionRequest = {
                documentNo: viewModel?.no,
                documentType: viewModel?.documentType,
                open: viewModel?.posted === null,
                action: "Delete"
            };
            const response = await authService.post<ActionResponseResult>("/inventoryApi/DocumentAction", body);
            showSnackbar(`${t("dialog.deletedSuccessfully")}`, "success");
            setIsLoading(false);
            navigate({ path: "/inventory/journal/list", params: { open: true } });
        } catch (error: any) {
            console.error("Error during Cancel:", error);
            showSnackbar(`${t("common.failedToCancel")} : ${error.response?.data ?? error.message}`, "error");
        }
        finally {
            setIsLoading(false);
        }
    };

    //const onCopy = async () => {
    //    setIsLoading(true);
    //    if (!window.confirm(t("dialog.areYouSure"))) return;
    //    try {
    //        console.log("Copying...");
    //        const body: ActionRequest = {
    //            documentNo: viewModel?.no,
    //            documentType: viewModel?.documentType,
    //            open: viewModel?.posted === null,
    //            action: "Copy"
    //        };
    //        const response = await authService.post<ActionResponseResult>("/inventoryApi/DocumentAction", body);
    //        console.log("Copy completed:", response);
    //        setIsLoading(false);
    //        navigate({ path: "/inventory/journals/edit", params: { documentNo: response.result } });
    //    } catch (error: any) {
    //        console.error("Error during Copy:", error);
    //        showSnackbar(`${t("common.failedToCopy")} : ${error.response?.data ?? error.message}`, "error");
    //    }
    //    finally {
    //        setIsLoading(false);
    //    }
    //};



    const handleAdd = async (overrideBarcode?: string) => {
        if (!viewModel) {
            return false;
        }

        if (!viewModel.locationCode || viewModel.locationCode.length === 0) {
            showSnackbar(t("dialog.missingLocation"), "error");
            return false;
        }

        const barcodeValue = overrideBarcode ?? viewModel?.barcodeToAdd ?? null;
        const itemNoValue = overrideBarcode ? null : viewModel?.itemNoToAdd ?? null;
        const hasBarcode = !!barcodeValue && barcodeValue.trim().length > 0;
        const hasItem = !!itemNoValue && itemNoValue.trim().length > 0;

        if (!hasItem && !hasBarcode) {
            showSnackbar(t("dialog.noItemOrBarcode"), "warning");
            return false;
        }

        if (viewModel.hasChanges) {
            showHasChangesError();
            return false;
        }

        showSnackbar(t("dialog.addingItems"), "info");

        const body: InventoryJournalAddItemRequest = {
            documentNo: viewModel?.no,
            barcode: barcodeValue,
            itemNo: itemNoValue,
            variantId: overrideBarcode ? viewModel?.variantIdtoAdd ?? null : viewModel?.variantIdtoAdd ?? null
        };

        setIsLoading(true);
        try {
            await authService.post("/inventoryApi/AddLine", body);

            setViewModel((prev) => {
                if (!prev) {
                    return prev;
                }

                if (overrideBarcode) {
                    return {
                        ...prev,
                        barcodeToAdd: overrideBarcode
                    };
                }

                return {
                    ...prev,
                    barcodeToAdd: null,
                    itemNoToAdd: null,
                    variantIdtoAdd: null
                };
            });
            setReloadLines(true);

        } catch (error: any) {
            console.error("Error adding line:", error);
            showSnackbar(`${t("dialog.addLineError")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            setIsLoading(false);
            setReloadLines(false);
            if (showAddItems) barcodeRef.current?.focus();
        }


    };


    const scanBarcode = useCallback(async (overrideBarcode?: string) => {
        if (!viewModel) {
            return false;
        }

        if (!viewModel.locationCode || viewModel.locationCode.length === 0) {
            showSnackbar(t("dialog.missingLocation"), "error");
            return false;
        }

        const barcodeValue = overrideBarcode;
        const hasBarcode = !!barcodeValue && barcodeValue.trim().length > 0;

        if (!hasBarcode) {
            showSnackbar(t("dialog.noItemOrBarcode"), "warning");
            return false;
        }

        if (viewModel.hasChanges) {
            showHasChangesError();
            return false;
        }

        showSnackbar(t("dialog.addingItems"), "info");

        const body: InventoryJournalLineViewModel = {
            documentNo: viewModel?.no,
            barcode: barcodeValue,
            increment: true
        };

        setIsLoading(true);
        let wasSuccessful = false;
        try {
            await authService.post("/inventoryApi/CreateOrUpdateLine", body);
            wasSuccessful = true;
            setReloadLines(true);
        } catch (error: any) {
            console.error("Error adding line:", error);
            showSnackbar(`${t("dialog.addLineError")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            setIsLoading(false);
            setReloadLines(false);
        }

        return wasSuccessful;
    }, [authService, showSnackbar, t, viewModel, showHasChangesError]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            void handleAdd();
        }
    };

    const handleScannerOpen = useCallback(() => {
        setIsScannerOpen(true);
    }, []);

    const handleScannerClose = useCallback(() => {
        setIsScannerOpen(false);
    }, []);

    const handleBarcodeScan = useCallback((code: string) => scanBarcode(code), [scanBarcode]);
    const showAddItemsWithCheck = (checked: boolean) => {

        if (checked && viewModel?.hasChanges === true) {
            showHasChangesError();
            return;
        }

        if (checked && (!viewModel?.locationCode || viewModel?.locationCode.length === 0)) {
            showSnackbar(t("dialog.missingLocation"), "error");
            return;
        }
        setShowAddItems(checked);
    }
    type PostingInputModal = {
        open: boolean;
        onClose: () => void;
        onPost: (mode: number) => void;
    };
    const PostingModeModal = memo(({ open, onClose, onPost }: PostingInputModal) => {
        return (
            <Dialog open={open} onClose={onClose}>
                <DialogTitle sx={{ textAlign: "center" }}>{t("common.selectPostingMode")}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} direction="column" alignItems="center">
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={() => {
                                onPost(0)
                            }}
                            color="primary"
                        >
                            {t("common.postIncluded")}
                        </Button>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={() => {
                                onPost(1)
                            }}
                            color="primary"
                        >
                            {t("common.includeAndZero")}
                        </Button>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>{t("common.cancel")}</Button>

                </DialogActions>
            </Dialog>
        );
    });

    if (!viewModel) {
        return <Typography>{t("common.loadingdotdot")}</Typography>;
    }


    return (

        <CenteredCard
            ref={toolbarRef}
            title={title}
            handleSave={history == false ? handleSave : undefined}
            handleCancel={cancelChanges}
            isLoading={isLoading}
            hasChanges={viewModel.hasChanges ?? false}
        >
            <PostingModeModal open={isPostingModalOpen} onClose={() => setIsPostingModalOpen(false)} onPost={onPost} />

            <Box sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                maxWidth: "fit-content",
                mb: 2
            }}>
                {(!history || viewModel.documentType == 3) && (
                    <DefaultActions
                    onPost={history ? undefined : onPost}
                    onDelete={history ? undefined : onDelete}
                        //  onCopy={onCopy}
                    onSave={history ? undefined : handleSave}
                        customActions={[
                            ...((viewModel?.documentType == 3 && history)
                                ? [
                                    {
                                        label: t("common.createItemlabels"), // or any text you prefer
                                        onClick: () => createLabelJournal(viewModel?.no),
                                    },
                                ]
                                : []),
                        ]}
                        
                    />
                )}
            </Box>

            {isLoading && <Typography> {t("common.loadingdotdot")}</Typography>}
            {/* Button to open SalesDocumentLines modal */}

            <Grid container spacing={2} >
                <Grid item xs={documentType == 2 ? 12 : 6}>
                    <TextField
                        label={t("common.documentNo")}
                        value={viewModel.no}
                        fullWidth
                        disabled={true}
                        margin="normal"
                    />





                </Grid>
                <Grid item xs={6}>
                    <FormControl fullWidth margin="normal">
                        <LocationAutoComplete
                            key={viewModel.locationCode || null}
                            valueId={viewModel.locationCode || null}
                            onChange={(selected) => handleChange("locationCode", selected?.value || "")}
                            disabled={history == true}
                            infoText={t("help.adjustLocation")}
                            error={!viewModel.locationCode || viewModel.locationCode.length == 0}
                        />

                    </FormControl>


                </Grid>
                {documentType == 2 && (
                    <Grid item xs={6}>
                        <FormControl fullWidth margin="normal">
                            <LocationAutoComplete
                                label={t("common.toLocation")}
                                key={viewModel.targetLocationCode || null}
                                valueId={viewModel.targetLocationCode || null}
                                onChange={(selected) => handleChange("targetLocationCode", selected?.value || "")}
                                disabled={history == true}
                                infoText={t("help.targetLocation")}
                                error={!viewModel.targetLocationCode || viewModel.targetLocationCode.length == 0}
                            />

                        </FormControl>


                    </Grid>

                )}
                <Grid item xs={6} >


                    <TextField
                        label={t("common.description")}
                        type="text"
                        value={viewModel.description}
                        onChange={(e) => handleChange("description", e.target.value)}
                        fullWidth
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        disabled={history == true}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <InfoButton infoText={t("help.journalDescription")} />
                                </InputAdornment>
                            ),
                        }}
                    />


                </Grid>
                <Grid item xs={6} >


                    <TextField
                        label={t("common.inventoryDate")}
                        type="date"
                        value={viewModel.applyDate?.split("T")[0]}
                        onChange={(e) => handleChange("applyDate", e.target.value)}
                        fullWidth
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        disabled={history == true}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <InfoButton infoText={t("help.inventoryDate")} />
                                </InputAdornment>
                            ),
                        }}
                    />


                </Grid>
                {history == false && (
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showAddItems}
                                    onChange={(e) => showAddItemsWithCheck(e.target.checked)}
                                />
                            }
                            label={t("common.addItems")}
                        />
                    </Grid>
                )}
                {/* Billing Information Section */}
                {showAddItems && (
                    <>
                        <Box
                            sx={{
                                backgroundColor: "secondary.main", // Light grey background
                                padding: 3,
                                borderRadius: 2,

                                width: "100%"

                            }}
                        >

                            <Grid container spacing={2} >
                                {!isLoading && (
                                    <>
                                        <Grid item xs={12} lg={6}>

                                            <FormControl fullWidth>
                                                <ItemAutoComplete
                                                    key={viewModel.itemNoToAdd}
                                                    valueId={viewModel.itemNoToAdd}
                                                    onKeyDown={handleKeyDown}
                                                    onChange={(selected) => {
                                                        handleChanges({ itemNoToAdd: selected?.value || null, variantIdtoAdd: null });
                                                    }}
                                                    infoText={t("help.addItemNo")}
                                                    disabled={history}
                                                />

                                            </FormControl>

                                        </Grid>


                                        <Grid item xs={12} lg={6}>

                                            <FormControl fullWidth>
                                                <VariantAutoComplete
                                                    key={viewModel?.variantIdtoAdd}
                                                    onKeyDown={handleKeyDown}
                                                    itemNo={viewModel.itemNoToAdd || ""}
                                                    onChange={(selected) => handleChange("variantIdtoAdd", selected?.id || null)}
                                                    valueId={viewModel?.variantIdtoAdd}
                                                    disabled={history}
                                                />
                                            </FormControl>

                                        </Grid>
                                        <Grid item xs={12} >


                                            <TextField
                                                inputRef={barcodeRef}
                                                onKeyDown={handleKeyDown}
                                                label={t("common.barcode")}
                                                type="text"
                                                value={viewModel.barcodeToAdd}
                                                onChange={(e) => handleChange("barcodeToAdd", e.target.value)}
                                                fullWidth
                                                margin="normal"
                                                InputLabelProps={{ shrink: true }}
                                                disabled={history}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <InfoButton infoText={t("help.addBarcode")} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />


                                        </Grid>
                                    </>
                                )}

                                <Grid item xs={12} >
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                        <Button variant="contained" onClick={() => { void handleAdd(); }} disabled={isLoading || history}>
                                            {t("common.add")}
                                        </Button>
                                        <Button variant="contained" startIcon={<DocumentScannerIcon />} onClick={handleScannerOpen} disabled={isLoading || history}>
                                            {t("common.scanBarcode")}
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Box>
                        <ScannerDialog
                            open={isScannerOpen}
                            onClose={handleScannerClose}
                            onScan={handleBarcodeScan}
                        />
                    </>
                )}







                <Box sx={{
                    padding: "16px", width: "98vw",
                    borderRadius: 2
                }}>
                    <Grid item xs={12}>



                        <InventoryJournalLines documentNo={documentNo} documentType={documentType} history={history} reloadLines={reloadLines} locationCode={viewModel?.locationCode} headerHasChanges={viewModel.hasChanges} setShowChangesError={showHasChangesError} />


                    </Grid>
                </Box>
            </Grid>


            <Box sx={{ marginTop: 2, textAlign: "center" }}>

                {/* Button to open Additional Fields modal */}
                {/*<Button variant="outlined" onClick={toggleFieldsModal} sx={{ marginBottom: 2 }}>*/}
                {/*    Open Additional Fields*/}
                {/*</Button>*/}
                <Button variant="contained" onClick={handleSave} disabled={isLoading || history} sx={{ marginRight: 2 }}>
                    {t("common.save")}
                </Button>
                <Button variant="outlined" onClick={goBack}>
                    {t("common.back")}
                </Button>
            </Box>



        </CenteredCard>
    );
};

export default InventoryJournalHeader;
